import { FC, useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';

import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Heading,
  Stack,
  Text,
} from '@eluve/components';

const DEFAULT_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

export interface ChartRendererProps {
  chartType: 'bar' | 'line' | 'area' | 'pie';
  data: Array<Record<string, string | number>>;
  dataKeys: string[];
  nameKey?: string;
  title?: string;
  description?: string;
  config?: Record<string, { label: string; color?: string }>;
  xAxisKey?: string;
  stacked?: boolean;
}

export const ChartRenderer: FC<ChartRendererProps> = ({
  chartType,
  data,
  dataKeys,
  nameKey,
  title,
  description,
  config,
  xAxisKey,
  stacked = false,
}) => {
  const chartConfig = useMemo<ChartConfig>(() => {
    const result: ChartConfig = {};
    dataKeys.forEach((key, i) => {
      const cssKey = toCssKey(key);
      const entry = {
        label: config?.[key]?.label ?? key,
        color:
          config?.[key]?.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      };
      result[key] = entry;
      // Add CSS-safe alias so ChartStyle generates a valid --color-* variable
      if (cssKey !== key) {
        result[cssKey] = entry;
      }
    });
    return result;
  }, [dataKeys, config]);

  const resolvedXAxisKey = xAxisKey ?? detectXAxisKey(data, dataKeys);

  return (
    <Stack gap={1} align="stretch">
      {(title || description) && (
        <Stack>
          {title && <Heading size="lg">{title}</Heading>}
          {description && (
            <Text size="xs" color="tertiary">
              {description}
            </Text>
          )}
        </Stack>
      )}
      <ChartContainer config={chartConfig} className="h-[300px] w-full">
        {chartType === 'pie' ? (
          <PieChart>
            <ChartTooltip content={<ChartTooltipContent hideLabel />} />
            <Pie
              data={data}
              dataKey={dataKeys[0]!}
              nameKey={nameKey ?? resolvedXAxisKey}
              innerRadius="40%"
            >
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                />
              ))}
            </Pie>
            <ChartLegend
              content={
                <ChartLegendContent nameKey={nameKey ?? resolvedXAxisKey} />
              }
            />
          </PieChart>
        ) : chartType === 'bar' ? (
          <BarChart accessibilityLayer data={data}>
            <CartesianGrid vertical={false} />
            {resolvedXAxisKey && (
              <XAxis
                dataKey={resolvedXAxisKey}
                tickLine={false}
                tickMargin={10}
                axisLine={false}
              />
            )}
            <YAxis tickLine={false} axisLine={false} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {dataKeys.map((key) => (
              <Bar
                key={key}
                dataKey={key}
                fill={`var(--color-${toCssKey(key)})`}
                radius={4}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </BarChart>
        ) : chartType === 'line' ? (
          <LineChart accessibilityLayer data={data}>
            <CartesianGrid vertical={false} />
            {resolvedXAxisKey && (
              <XAxis
                dataKey={resolvedXAxisKey}
                tickLine={false}
                tickMargin={10}
                axisLine={false}
              />
            )}
            <YAxis tickLine={false} axisLine={false} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {dataKeys.map((key) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={`var(--color-${toCssKey(key)})`}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        ) : (
          <AreaChart accessibilityLayer data={data}>
            <CartesianGrid vertical={false} />
            {resolvedXAxisKey && (
              <XAxis
                dataKey={resolvedXAxisKey}
                tickLine={false}
                tickMargin={10}
                axisLine={false}
              />
            )}
            <YAxis tickLine={false} axisLine={false} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {dataKeys.map((key) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                fill={`var(--color-${toCssKey(key)})`}
                stroke={`var(--color-${toCssKey(key)})`}
                fillOpacity={0.3}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </AreaChart>
        )}
      </ChartContainer>
    </Stack>
  );
};

/** Sanitize a data key for use in CSS custom property names (spaces etc. are invalid). */
function toCssKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9_-]/g, '-');
}

/** Pick the first key in the data that isn't in dataKeys (i.e. the label/category column). */
function detectXAxisKey(
  data: Array<Record<string, string | number>>,
  dataKeys: string[],
): string | undefined {
  if (!data.length) return undefined;
  const dataKeySet = new Set(dataKeys);
  return Object.keys(data[0]!).find((k) => !dataKeySet.has(k));
}
